import type { FormKitNode, FormKitEvent } from '@formkit/core'
import type { ComponentInternalInstance } from 'vue'

//Přidá do FormKit listu další item (bez zadání indexu ho přidá na konec pole)
const add = async <T>(node: FormKitNode<T[]>, newValue: T, index?: number) => {
  const value = node.value.slice()
  value.splice((typeof index === 'number' ? index : value.length) + 1, 0, newValue)
  await node.input(value, false)
}

//Oddělá z FormKit listu item z poslaného indexu
const remove = async <T>(node: FormKitNode<T[]>, index: number) => {
  const value = node.value.slice()
  value.splice(index, 1)
  await node.input(value, false)
}

const up = async <T>(node: FormKitNode<T[]>, index: number) => {
  const value = node.value.slice()
  value.splice(index - 1, 0, value.splice(index, 1)[0])
  await node.input(value, false)
}

const down = async <T>(node: FormKitNode<T[]>, index: number) => {
  const value = node.value.slice()
  value.splice(index + 1, 0, value.splice(index, 1)[0])
  await node.input(value, false)
}

const move = async <T>(node: FormKitNode<T[]>, newIndex: number, oldIndex: number) => {
  const value = node.value.slice()
  if (
    oldIndex < 0 ||
    oldIndex >= value.length ||
    newIndex < 0 ||
    newIndex >= value.length ||
    oldIndex === newIndex
  ) {
    return // Invalid move, do nothing
  }
  const [movedItem] = value.splice(oldIndex, 1)
  value.splice(newIndex, 0, movedItem!)
  await node.input(value, false)
}

const isValid = (node: FormKitNode, paths: string[]) => {
  let isValid = true
  paths.forEach((path) => {
    nextTick(() => {
      const findedNode = node.at(path)
      if (findedNode && isValid) {
        isValid = findedNode.context.state.valid
      }
    })
  })
  return isValid
}

const findParentTabNames = (
  instance: ComponentInternalInstance | null,
  componentName: string = 'TabPanel'
) => {
  let parent = instance?.parent
  const tabNames = []
  let sidebarName = null
  let accordionName = null
  while (parent) {
    if (parent.type.name === 'Drawer') {
      sidebarName = parent?.attrs['sidebar-name']
    }
    if (parent.type.name === 'AppDatagridBulkActionsAccordion') {
      accordionName = `${parent?.props?.parentName ? `${parent?.props?.parentName}-` : ''}${parent?.props?.name}`
    }
    if (parent.type.name === componentName) {
      tabNames.push(parent?.props.value)
    }
    parent = parent.parent
  }
  return { tabNames, sidebarName, accordionName }
}

export const pathCheckRegex = (paths: string[], path: string) => {
  return !!paths?.some((pattern) => {
    const regexStr = '^' + pattern.replace(/\./g, '\\.').replace(/\*/g, '\\d+') + '$'
    const regex = new RegExp(regexStr)
    return regex.test(path)
  })
}

export const useFormKit = () => {
  let tabNode: FormKitNode
  const tabListenerKeys: string[] = []
  let inputListener: string
  let inputNode: FormKitNode
  const dirtyPlugin = (node: FormKitNode) => {
    inputNode = node
    inputListener = inputNode.on('updateValue', () => {
      const formNode = node.at('$root') as FormKitNode
      if (formNode.context) {
        formNode.context.isUserDirty = true
      }
    })
  }
  const tabPlugin = (node: FormKitNode) => {
    nextTick(() => {
      const accordionName = node.context?.accordionName
      const names = node.context?.tabNames ?? [accordionName]
      const sidebarName = node.context?.sidebarName
      names.forEach((name) => {
        if (name || typeof name === 'number') {
          const formNode = node.at('$root') as FormKitNode
          tabNode = node

          tabNode.ledger.count('visible', (message) => message.visible && message.blocking)

          const updateVisibleCount = ({ origin }: FormKitEvent) => {
            if (origin.type !== 'list' && origin.type !== 'group') {
              let errorsCount = node.context ? node.ledger.value('visible') : 0
              // Pokud je náhled, tak ledger.count() vrátí falling message která sice nesplnuje filtr (visible a blocking), ale stejně ji započítá, proto je nutné pro filtrovat ještě jednou
              if (errorsCount) {
                errorsCount = node.store.reduce((acc, message) => {
                  if (message.visible && message.blocking) {
                    acc += 1
                  }
                  return acc
                }, 0)
              }
              if (formNode.context) {
                if (!formNode.context?.errorsInputs) {
                  formNode.context.errorsInputs = {}
                }
                if (!formNode.context?.errorsInputsSidebars) {
                  formNode.context.errorsInputsSidebars = {}
                }
              }
              if (formNode.context && formNode.context?.errorsInputs) {
                if (!formNode.context.errorsInputs[name]) {
                  formNode.context.errorsInputs[name] = {}
                }
                formNode.context.errorsInputs[name][origin?.context?.id] = errorsCount
                if (sidebarName) {
                  if (!formNode.context?.errorsInputsSidebars?.[sidebarName]) {
                    formNode.context.errorsInputsSidebars[sidebarName] = {
                      [origin?.context?.id]: errorsCount,
                    }
                  } else {
                    formNode.context.errorsInputsSidebars[sidebarName][origin?.context?.id] =
                      errorsCount
                  }
                }
              }
              const errorsTabCount = Object.values(
                formNode?.context?.errorsInputs?.[name] ?? {}
              ).some((item) => item > 0)
              const errorsSidebarCount = Object.values(
                formNode?.context?.errorsInputsSidebars?.[sidebarName] ?? {}
              ).some((item) => item > 0)

              if (formNode.context && sidebarName) {
                if (!formNode.context.errorsSidebars) {
                  formNode.context.errorsSidebars = {
                    [sidebarName]: errorsSidebarCount,
                  }
                } else {
                  formNode.context.errorsSidebars[sidebarName] = errorsSidebarCount
                }
              }

              if (formNode.context && !formNode.context.errorsTabs) {
                formNode.context.errorsTabs = {
                  [name]: errorsTabCount,
                }
              } else if (formNode.context) {
                formNode.context.errorsTabs[name] = errorsTabCount
              }

              if (formNode.context && !formNode.context.errorsAccordions && accordionName) {
                formNode.context.errorsAccordions = {
                  [accordionName]: errorsTabCount,
                }
              } else if (formNode.context && accordionName) {
                formNode.context.errorsAccordions[accordionName] = errorsTabCount
              }

              if (node.context) {
                node.context.errorCount = errorsCount
              }
            }
          }
          tabListenerKeys.push(
            tabNode.on('destroying', ({ origin }) => {
              if (formNode.context?.errorsInput?.[name]) {
                delete formNode.context.errorsInputs[name][origin.props.id]
              }
            })
          )

          tabListenerKeys.push(tabNode.on('message-added', updateVisibleCount))
          tabListenerKeys.push(tabNode.on('message-updated', updateVisibleCount))
          tabListenerKeys.push(tabNode.on('message-removed', updateVisibleCount))
          tabListenerKeys.push(
            tabNode.on('updateValue', ({ origin }) => {
              const formNode = origin.at('$root')
              if (formNode?.context) {
                formNode.context.submted = false
              }
            })
          )
        }
      })
    })
  }
  onBeforeUnmount(() => {
    if (tabNode) {
      tabListenerKeys.forEach((key) => {
        tabNode?.off(key)
      })
    }
    if (inputNode && inputListener) {
      inputNode.off(inputListener)
    }
  })

  return { add, remove, up, down, move, isValid, tabPlugin, dirtyPlugin, findParentTabNames }
}
